// Affiliate tags on shopping searches: the store credits Search Already a
// small cut of what's bought, at no cost to the shopper. Only the store's own
// tracking parameters are added; nothing about the person or the search goes
// anywhere new. Off when the IDs aren't configured (self-hosted copies, dev,
// tests) or when the person turns "shopping links" off in Settings.

export interface AffiliateIds {
  /** eBay Partner Network campaign id (10 digits). */
  ebay?: string;
  /** Amazon Associates tracking tag (`name-20`), amazon.com only. */
  amazon?: string;
}

export const AFFILIATE_IDS: AffiliateIds = {
  ebay: import.meta.env.VITE_EBAY_CAMPID || undefined,
  amazon: import.meta.env.VITE_AMAZON_TAG || undefined,
};

export type Store = 'eBay' | 'Amazon';

// eBay Partner Network link parameters for ebay.com (rotation id is per
// country site; only the US site is tagged).
const EBAY_US = {
  mkcid: '1',
  mkrid: '711-53200-19255-0',
  siteid: '0',
  toolid: '10001',
  mkevt: '1',
};

const isEbayUs = (u: URL) => u.hostname === 'www.ebay.com';
// Store searches only: an order-history link has nothing to buy.
const isAmazonUs = (u: URL) =>
  u.hostname === 'www.amazon.com' && !u.pathname.startsWith('/your-orders');

/** Adds the store's affiliate tag to a result URL, when one applies. */
export function withAffiliate(
  url: string,
  enabled: boolean,
  ids: AffiliateIds = AFFILIATE_IDS
): string {
  if (!enabled) return url;
  let u: URL;
  try {
    u = new URL(url);
  } catch {
    return url;
  }
  if (ids.ebay && /^\d{10}$/.test(ids.ebay) && isEbayUs(u)) {
    for (const [k, v] of Object.entries(EBAY_US)) u.searchParams.set(k, v);
    u.searchParams.set('campid', ids.ebay);
    return u.toString();
  }
  if (ids.amazon && /^[\w-]{1,40}-\d{2}$/.test(ids.amazon) && isAmazonUs(u)) {
    u.searchParams.set('tag', ids.amazon);
    return u.toString();
  }
  return url;
}

/** Which store's tag a URL carries, for the note shown beside it. */
export function affiliateStore(
  url: string,
  ids: AffiliateIds = AFFILIATE_IDS
): Store | undefined {
  try {
    const u = new URL(url);
    if (ids.ebay && isEbayUs(u) && u.searchParams.get('campid') === ids.ebay)
      return 'eBay';
    if (ids.amazon && isAmazonUs(u) && u.searchParams.get('tag') === ids.amazon)
      return 'Amazon';
  } catch {
    // Not a URL.
  }
  return undefined;
}

/** True when this build tags anything at all (so the setting is worth showing). */
export const AFFILIATE_ACTIVE = Boolean(
  AFFILIATE_IDS.ebay || AFFILIATE_IDS.amazon
);

/** The tip page (Ko-fi), when this build has one. */
export const TIP_URL = /^https:\/\/\S+$/.test(
  import.meta.env.VITE_TIP_URL ?? ''
)
  ? import.meta.env.VITE_TIP_URL
  : undefined;
